<script setup lang="ts">
  import { ref, watch } from "vue";
  import { Dialog, Button, Text } from "@ui-kit";
  import { type Product as Item, getListBySeriaApi, ResponseCode } from "@src/api";
  import { ProductMiniCard } from "../ProductMiniCard";
  import { default as SearchItemUHFIconLocator } from "./SearchItemUHFIconLocator.vue";

  const { is_open, seria_guid, img_url, name, article, color } = defineProps<{
    seria_guid: string;
    is_open: boolean;
    img_url: string;
    name: string;
    article: string;
    color: string;
  }>();
  const emit = defineEmits<{
    (e: "click:close"): void;
    (e: "click:size", item: Item): void;
  }>();

  const size_list = ref<Item[]>([]);
  const is_loading = ref(false);

  watch(
    () => is_open,
    async () => {
      if (!is_open) return;

      is_loading.value = true;
      const result = await getListBySeriaApi({ seria_guid });

      if (result.code === ResponseCode.OK) {
        size_list.value = result.items.filter((item) => !item.is_virtual);
      }

      is_loading.value = false;
    },
  );
</script>

<template>
  <Dialog @close="emit('click:close')" :isOpen="is_open && !is_loading" data-testid="search-item-uhf-dialog">
    <div class="search-item-uhf">
      <div class="search-item-uhf__icon">
        <SearchItemUHFIconLocator />
      </div>
      <div class="search-item-uhf__header">
        <Text font="body-bold-18">{{ $t("Поиск товара локатором") }}</Text>
        <Text font="body-regular-14">{{ $t("Выберите размер товара") }}</Text>
      </div>
      <div class="search-item-uhf__content">
        <ProductMiniCard :image="img_url" :name="name" :article="article" :color="color" />
        <div class="search-item-uhf__size-list">
          <Button v-for="item in size_list" :key="item.item_id" variant="tertiary" @click="emit('click:size', item)">
            {{ item.size }}
          </Button>
        </div>
      </div>
      <div class="search-item-uhf__footer">
        <Button variant="secondary" @click="emit('click:close')">{{ $t("Отменить") }}</Button>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
  .search-item-uhf {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px 20px 20px;
    width: 330px;
    max-height: 95vh;
    overflow: hidden;
  }

  .search-item-uhf__icon {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .search-item-uhf__header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
  }

  .search-item-uhf__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;

    & :deep(.item) {
      padding: 12px;
      border-radius: 16px;
      background-color: var(--sf-base-background-primary);
    }
  }

  .search-item-uhf__size-list {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    overflow-y: auto;

    & :deep(.button) {
      width: calc(50% - 4px);
    }
  }

  .search-item-uhf__footer {
    margin-top: 8px;

    & :deep(.button) {
      width: 100%;
    }
  }
</style>
